import { asWidgetProps } from 'Src/types/widget-helpers';
/* eslint-disable clubmed/no-fake-component */
import React, { type ComponentProps } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { t } from 'Src/i18n';
import type { LineItem } from 'Views/booking/receipt/paid-services-list/PaidServicesList';
import { PaidServicesList } from 'Views/booking/receipt/paid-services-list/PaidServicesList';
import { CollapsePanel } from 'Components/frameworks/collapse-panel/CollapsePanel';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { ICONS } from 'Components/utils/icon/icons';
import { themes } from './HomeDetails.theme';
export type Service = {
  title: string;
  description?: string;
  price: Price;
};
type Client = {
  name: string;
  services: Array<Service>;
  total: Price;
};
type Props = {
  people: {
    adults: Array<Client>;
    children?: Array<Client>;
  };
  homeIndex?: number;
  proposal: Proposal;
  items: Array<LineItem>;
  i18n: any;
  // base i18n
  resort: Product;
  theme?: keyof typeof themes;
};
export const HomeDetails = ({
  people,
  homeIndex,
  proposal,
  items,
  i18n,
  resort,
  theme = 'default',
}: Props) => {
  const { thCollapse, thCollapseTheme, thCollapseIconCollapse, thCollapseIconExpand } =
    themes[theme]();

  const getCollapsePanelTitle = (client: Client) => {
    return (
      <span className="flex w-full justify-between">
        <span>{client.name}</span>
        <span className="whitespace-nowrap text-16 font-bold text-saffron">
          {client.total.formattedValue}
        </span>
      </span>
    );
  };

  const getHomeDetailsServices = (service: Service, index) => {
    const hasDescription = !!service.description;
    return (
      <div
        className={classnames(
          'mx-5 h-50 pb-5 pt-10 text-12 leading-tight',
          {
            'border-t-1 border-dashed border-grey': index !== 0,
          },
          {
            'items-end': hasDescription,
          },
          {
            'items-center': !hasDescription,
          },
        )}
        key={index}
      >
        <div className="flex justify-between">
          <div>
            <div>{service.title}</div>
            {hasDescription && <div>{service.description}</div>}
          </div>
          <div className="whitespace-nowrap font-bold text-saffron">
            {service.price.formattedValue}
          </div>
        </div>
      </div>
    );
  };

  const getHomeDetailsTotalPricePerClient = (client: Client) => {
    return (
      <div className="flex h-50 items-start justify-between border-t-1 border-solid border-saffron px-5 pb-5 pt-10 text-12 leading-tight">
        <div>
          <div className="font-bold text-saffron">
            {t('booking.priceDetails.packagePrice.priceTotalByParticipant')}
          </div>
          <div>{t('booking.priceDetails.packagePrice.fees.taxesAndFeesIncludedLabel')}</div>
        </div>
        <div className="whitespace-nowrap font-bold text-saffron">
          {client.total.formattedValue}
        </div>
      </div>
    );
  };

  const hasChildren = people?.children && people.children.length > 0;
  const numberOfAdults = people?.adults.length;
  const numberOfChildren = hasChildren ? Number(people.children?.length) : 0;
  return (
    <div data-testid="HomeDetails">
      <div className="px-30 pt-30">
        {homeIndex && (
          <p className="font-brand text-20 text-saffron">
            {t('booking.priceDetails.packagePrice.householdNumber', {
              householdNnumber: homeIndex.toString().padStart(2, '0'),
            })}
          </p>
        )}
        <p className="text-12">
          <TemplateString
            {...asWidgetProps<ComponentProps<typeof TemplateString>>({
              template: hasChildren
                ? t('booking.priceDetails.packagePrice.selectedParticipants')
                : t('booking.priceDetails.packagePrice.selectedAdults'),
              values: {
                nbAdults: numberOfAdults,
                nbChildren: numberOfChildren,
              },
            })}
          />
        </p>
      </div>
      <div className="px-30">
        {people.adults.map((adult) => (
          <CollapsePanel
            key={adult.name}
            {...asWidgetProps<ComponentProps<typeof CollapsePanel>>({
              className: thCollapse,
              collapseIconName: thCollapseIconCollapse,
              expandIconName: thCollapseIconExpand,
              theme: thCollapseTheme,
              title: getCollapsePanelTitle(adult),
              iconName: ICONS.PEOPLE,
              iconSize: '0.9375rem',
            })}
          >
            {adult.services.map((service, index) => getHomeDetailsServices(service, index))}
            {getHomeDetailsTotalPricePerClient(adult)}
          </CollapsePanel>
        ))}
        {hasChildren &&
          people.children?.map((child) => (
            <CollapsePanel
              key={child.name}
              {...asWidgetProps<ComponentProps<typeof CollapsePanel>>({
                className: thCollapse,
                collapseIconName: thCollapseIconCollapse,
                expandIconName: thCollapseIconExpand,
                theme: thCollapseTheme,
                title: getCollapsePanelTitle(child),
                iconName: ICONS.PEOPLE,
                iconSize: '0.8125rem',
              })}
            >
              {child.services.map((service, index) => getHomeDetailsServices(service, index))}
              {getHomeDetailsTotalPricePerClient(child)}
            </CollapsePanel>
          ))}
      </div>
      <div className="mt-10 px-40">
        <PaidServicesList
          {...asWidgetProps<ComponentProps<typeof PaidServicesList>>({
            proposal: proposal,
            i18n: i18n,
            items: items,
            resort: resort,
            isHomeDetails: true,
          })}
        />
      </div>
    </div>
  );
};
